// eslint-disable-next-line workspaces/require-dependency
import { actionBarMiddleware } from '@databases/redux/action-bar/middleware';
// eslint-disable-next-line workspaces/require-dependency
import { cellSelectionsNoOpMiddleware } from '@databases/redux/cellSelections/noOpMiddleware';
// eslint-disable-next-line workspaces/require-dependency
import { presenceMiddleware } from '@databases/redux/presence/middleware';
// eslint-disable-next-line workspaces/require-dependency
import { resolvedSelectionMiddleware } from '@databases/redux/rovo-referenced-content/middleware';
import { type MiddlewareArray } from '@reduxjs/toolkit';
import { batch as batchAdapter } from 'react-redux';
import { type Middleware, type StateFromReducersMapObject } from 'redux';
import { type ThunkMiddleware } from 'redux-thunk';

import { getThrottledBatchCallback, sleep } from '../utils';

import { type AsyncOperation, AsyncOperationStatus } from './async-operation';
// Value import required: used with typeof (line 30) and re-exported (line 22)
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { commonReducers } from './common-reducers';

export {
  AsyncOperationStatus,
  type AsyncOperation,
  validateStatus,
} from './async-operation';
export { commonReducers } from './common-reducers';
export {
  type ActionDependencyHelpers,
  type ActionWithDependencies,
  runActionWithDependencies,
  type SimpleStore,
} from './action-dependencies';

export type CommonRootState = StateFromReducersMapObject<typeof commonReducers>;

type CurriedGetDefaultMiddleware<S> = (options?: {
  serializableCheck?: boolean;
}) => MiddlewareArray<[ThunkMiddleware<S>, ...Middleware<object, S>[]]>;

export function getDefaultMiddleware<S>(): (
  getDefaultMiddleware: CurriedGetDefaultMiddleware<S>
) => MiddlewareArray<[ThunkMiddleware<S>, ...Middleware<object, S>[]]> {
  return (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      serializableCheck: false,
    }).concat(
      presenceMiddleware.middleware,
      resolvedSelectionMiddleware.middleware,
      actionBarMiddleware.middleware,
      cellSelectionsNoOpMiddleware
    );
  };
}

function noThrottle<T>(callback: () => T): Promise<T> {
  return Promise.resolve(callback());
}

/**
 * Loads an item if its load status is undefined or INITIAL. If its load status is PENDING, waits until the status changes.
 * The load operation is delayed using throttledBatch() if throttle is true.
 * @param selectStatus Should returns the current load status of the item
 * @param onStart Should mark the load status as PENDING. Is called if the load status was undefined or INITIAL.
 * @param load Loads the item. Is called if the load status was undefined or INITIAL.
 * @param onSuccess Should mark the load status as SUCCEEDED and put the item in the store. Is called if "load" returned successfully.
 * @param onError Should mark the load status as ERROR and put the error in the store. Is called if "load" raised an exception.
 * @param forceReload If this is true, the item is loaded regardless of its load status.
 */
export async function loadAsync<R>(
  selectStatus: () => AsyncOperation<any> | undefined,
  onStart: () => void,
  load: () => Promise<R>,
  onSuccess: (result: R) => void,
  onError: (error: any) => void,
  {
    forceReload = false,
    throttle = true,
  }: { forceReload?: boolean; throttle?: boolean } = {}
): Promise<void> {
  if (!forceReload) {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const status = await throttledBatch(() => {
        // fetch status
        const status = selectStatus();

        // if we have no status or the status is initial, start loading
        if ([undefined, AsyncOperationStatus.INITIAL].includes(status?.status)) {
          onStart();
        }

        return status;
      });

      if (status?.status === AsyncOperationStatus.PENDING) {
        // if we are pending, busy wait
        await sleep(50);
      } else if (
        [
          undefined,
          AsyncOperationStatus.INITIAL,
          AsyncOperationStatus.SUCCEEDED,
        ].includes(status?.status)
      ) {
        // if we're finished or didn't transition to pending, stop looping
        break;
      } else {
        return;
      }
    }

    // if we already succeeded, don't refetch
    if (selectStatus()?.status === AsyncOperationStatus.SUCCEEDED) {
      return;
    }
  }

  try {
    const result = await load();
    await (throttle ? throttledBatch : noThrottle)(async () => {
      await onSuccess(result);
    });
  } catch (error: any) {
    await (throttle ? throttledBatch : noThrottle)(() => {
      onError(error);
    });
    throw error;
  }
}

/**
 * Groups multiple action dispatches together. Runs the given callback function (which usually dispatches multiple redux actions)
 * and potentially pauses React renders during the execution of the callback (depending on the adapter set by setBatchAdapter()).
 * The frontend should set up react-redux's batch() as an adapter for this.
 */
export function batch(callback: () => void): void {
  isBatching = true;
  try {
    batchAdapter(callback);
  } finally {
    isBatching = false;
    onBatchEndListeners.forEach((listener) => listener());
    onBatchEndListeners = [];
  }
}

let isBatching = false;
let onBatchEndListeners: Array<() => void> = [];

/**
 * Delays the execution of the given callback if a Redux batch operation is currently running.
 * If no batch operation is running, the callback is called immediately. If a batch operation is running, the callback
 * is called synchronously as soon as the batch operation is finished.
 */
export function onBatchEnd(callback: () => void): void {
  if (!isBatching) {
    callback();
  } else {
    onBatchEndListeners.push(callback);
  }
}

let throttledBatchInternal = getThrottledBatchCallback<[() => void]>((callbacks) => {
  let result: any[];
  batch(() => {
    result = callbacks.map(([callback]) => callback());
  });
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return result!;
}, 250);

/**
 * Like batch(), but delays the execution of the callback function by a few milliseconds and then runs the whole
 * pile of callbacks in a single batch operation. This can be used when a lot of items need to be fetched from
 * a remote API to avoid a rerender of the app every single time an item is received.
 */
export async function throttledBatch<R>(callback: () => R): Promise<R> {
  return await throttledBatchInternal(callback);
}

export async function withoutThrottledBatching<T>(cb: () => Promise<T>): Promise<T> {
  const batchFunction = throttledBatchInternal;
  throttledBatchInternal = async (cb) => await cb();
  try {
    return await cb();
  } finally {
    throttledBatchInternal = batchFunction;
  }
}
